import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Award, Newspaper, ArrowRight } from 'lucide-react';

export default function NewsPage() {
  return (
    <div className="page-transition pt-28">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-12">
        <div className="max-w-3xl">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] block mb-2">
            Press &amp; Media
          </span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#F5F5F0] tracking-tighter leading-none mb-6">
            News
          </h1>
          <p className="text-[#A3A3A3] text-base leading-relaxed">
            Latest press coverage, features, and announcements from Phaeton Imagery.
          </p>
        </div>
      </section>

      {/* ===== FEATURED PRESS — Go!Entrepreneur Magazine ===== */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
        <div className="border border-[#C5A059]/30 bg-[#121212]">
          {/* Featured badge */}
          <div className="flex items-center gap-3 px-8 py-4 border-b border-white/10">
            <Award size={18} className="text-[#C5A059]" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">
              Featured Press
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Text content */}
            <div className="lg:col-span-5 p-8 lg:p-12 flex flex-col justify-center">
              <div className="mb-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] block mb-3">
                  Go!Entrepreneur Magazine
                </span>
                <h2 className="font-serif text-3xl lg:text-4xl text-[#F5F5F0] tracking-tight leading-tight mb-4">
                  Akanni Art Named Top 10 Diverse Businesses in Art &amp; Culture
                </h2>
                <p className="text-[#A3A3A3] leading-relaxed mb-6">
                  Phaeton Imagery by Akanni has been recognized by Go!Entrepreneur Magazine 
                  as one of the <span className="text-[#C5A059] font-medium">Top 10 Diverse 
                  Businesses in Art &amp; Culture</span>. The feature, published on pages 22–23 
                  of the magazine, highlights Akanni's distinctive approach to visual art — 
                  blending bold expressionism with digital innovation to create works that 
                  resonate across boundaries.
                </p>
                <p className="text-[#A3A3A3] leading-relaxed mb-8">
                  The recognition celebrates artists and cultural entrepreneurs who are 
                  pushing creative boundaries while building sustainable businesses that 
                  enrich their communities. Akanni's commitment to making art accessible 
                  through monthly auctions and limited-edition prints was cited as a model 
                  for emerging artists.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://heyzine.com/flip-book/cbb8ee6956.html#page/22"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0A0A0A] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#E0BE77] transition-colors"
                    data-testid="read-article-btn"
                  >
                    <Newspaper size={14} />
                    Read Article — Pages 22–23
                  </a>
                  <a
                    href="https://heyzine.com/flip-book/cbb8ee6956.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 border border-white/20 text-[#F5F5F0] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                    data-testid="full-magazine-btn"
                  >
                    <ExternalLink size={14} />
                    Full Magazine
                  </a>
                </div>
              </div>

              {/* Article highlight */}
              <div className="mt-8 p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                    <Award size={24} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-[#F5F5F0] font-medium text-sm mb-1">
                      "Akanni Art" — Pages 22 &amp; 23
                    </p>
                    <p className="text-[#525252] text-xs font-mono uppercase tracking-wider">
                      Top 10 Diverse Businesses in Art &amp; Culture
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Magazine Flipbook Embed */}
            <div className="lg:col-span-7 bg-black">
              <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                <iframe
                  src="https://heyzine.com/flip-book/cbb8ee6956.html#page/22"
                  title="Go!Entrepreneur Magazine — Akanni Art Feature"
                  className="absolute inset-0 w-full h-full border-0"
                  allowFullScreen
                  data-testid="magazine-embed"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECOND FEATURE — ATL Plus Magazine ===== */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
        <div className="border border-white/10 bg-[#121212]">
          <div className="flex items-center gap-3 px-8 py-4 border-b border-white/10">
            <Award size={18} className="text-[#C5A059]" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">
              Featured Press
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Magazine cover image */}
            <div className="lg:col-span-5 bg-black flex items-center justify-center p-8">
              <a
                href="https://www.atlplusmagazine.com/product/atl-plus-magazine-mar-april-2023-spring-into-action"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid="atl-cover-link"
              >
                <img
                  src="https://cdn.prod.website-files.com/610251166ef1596b27033b1a/659d7b7ca2fab2e20b69c9c9_71F6D344-4167-491F-99AE-DDFD8FD97095.jpeg"
                  alt="ATL Plus Magazine Mar-April 2023 — Spring Into Action"
                  className="w-full max-w-sm h-auto object-contain hover:scale-105 transition-transform duration-500"
                />
              </a>
            </div>

            {/* Text content */}
            <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col justify-center">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] block mb-3">
                ATL Plus Magazine &middot; Mar/April 2023
              </span>
              <h2 className="font-serif text-3xl lg:text-4xl text-[#F5F5F0] tracking-tight leading-tight mb-4">
                Akanni Art Featured in ATL Plus Magazine "Spring Into Action" Edition
              </h2>
              <p className="text-[#A3A3A3] leading-relaxed mb-6">
                Phaeton Imagery by Akanni was featured in the <span className="text-[#C5A059] font-medium">Mar/April 2023
                "Spring Into Action"</span> edition of ATL Plus Magazine — Atlanta's premier lifestyle and culture publication. 
                The feature spotlights Akanni's bold artistic vision and the growing influence of Phaeton Imagery 
                in the Southern art scene, showcasing how the studio blends expressive painting with digital 
                innovation to create works that move and inspire.
              </p>
              <p className="text-[#A3A3A3] leading-relaxed mb-8">
                ATL Plus Magazine celebrates the voices, creatives, and entrepreneurs shaping Atlanta's 
                cultural landscape. Being featured alongside the city's most dynamic figures is a testament 
                to the impact of Akanni's work beyond the canvas.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://www.atlplusmagazine.com/product/atl-plus-magazine-mar-april-2023-spring-into-action"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0A0A0A] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#E0BE77] transition-colors"
                  data-testid="atl-read-btn"
                >
                  <Newspaper size={14} />
                  View Magazine Issue
                </a>
                <a
                  href="https://www.atlplusmagazine.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-[#F5F5F0] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                  data-testid="atl-site-btn"
                >
                  <ExternalLink size={14} />
                  ATL Plus Magazine
                </a>
              </div>

              {/* Highlight box */}
              <div className="mt-8 p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                    <Newspaper size={24} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-[#F5F5F0] font-medium text-sm mb-1">
                      "Spring Into Action" — Mar/April 2023
                    </p>
                    <p className="text-[#525252] text-xs font-mono uppercase tracking-wider">
                      ATL Plus Magazine &middot; Atlanta Culture &amp; Lifestyle
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Press Highlights ===== */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
        <h3 className="font-serif text-2xl text-[#F5F5F0] mb-8">Press Highlights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Go!Entrepreneur card */}
          <a
            href="https://heyzine.com/flip-book/cbb8ee6956.html#page/22"
            target="_blank"
            rel="noopener noreferrer"
            className="group block border border-white/10 hover:border-[#C5A059]/30 transition-colors"
            data-testid="press-goentrepreneur"
          >
            <div className="p-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] block mb-3">
                Go!Entrepreneur Magazine
              </span>
              <h4 className="font-serif text-lg text-[#F5F5F0] mb-2 group-hover:text-[#C5A059] transition-colors">
                Top 10 Diverse Businesses in Art &amp; Culture
              </h4>
              <p className="text-[#A3A3A3] text-sm line-clamp-3 mb-4">
                Akanni Art recognized among the top diverse businesses making an impact 
                in the art and culture space.
              </p>
              <span className="inline-flex items-center gap-2 text-[#C5A059] font-mono text-[10px] uppercase tracking-[0.2em]">
                Read More <ExternalLink size={12} />
              </span>
            </div>
          </a>

          {/* ATL Plus Magazine card */}
          <a
            href="https://www.atlplusmagazine.com/product/atl-plus-magazine-mar-april-2023-spring-into-action"
            target="_blank"
            rel="noopener noreferrer"
            className="group block border border-white/10 hover:border-[#C5A059]/30 transition-colors"
            data-testid="press-atlplus"
          >
            <div className="p-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] block mb-3">
                ATL Plus Magazine
              </span>
              <h4 className="font-serif text-lg text-[#F5F5F0] mb-2 group-hover:text-[#C5A059] transition-colors">
                Spring Into Action — Mar/April 2023
              </h4>
              <p className="text-[#A3A3A3] text-sm line-clamp-3 mb-4">
                Akanni Art featured in Atlanta's premier lifestyle and culture publication, 
                spotlighting bold artistic vision in the Southern art scene.
              </p>
              <span className="inline-flex items-center gap-2 text-[#C5A059] font-mono text-[10px] uppercase tracking-[0.2em]">
                Read More <ExternalLink size={12} />
              </span>
            </div>
          </a>

          {/* Monthly Auction announcement card */}
          <Link
            to="/auction"
            className="group block border border-white/10 hover:border-[#C5A059]/30 transition-colors"
            data-testid="press-auction"
          >
            <div className="p-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] block mb-3">
                Announcement
              </span>
              <h4 className="font-serif text-lg text-[#F5F5F0] mb-2 group-hover:text-[#C5A059] transition-colors">
                Monthly Art Auction Now Live
              </h4>
              <p className="text-[#A3A3A3] text-sm line-clamp-3 mb-4">
                Bid on original artworks by Akanni. The highest bidder receives the 
                original piece and a personal meet &amp; greet with the artist.
              </p>
              <span className="inline-flex items-center gap-2 text-[#C5A059] font-mono text-[10px] uppercase tracking-[0.2em]">
                View Auction <ArrowRight size={12} />
              </span>
            </div>
          </Link>

          {/* Shop launch card */}
          <Link
            to="/shop"
            className="group block border border-white/10 hover:border-[#C5A059]/30 transition-colors"
            data-testid="press-shop"
          >
            <div className="p-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] block mb-3">
                Announcement
              </span>
              <h4 className="font-serif text-lg text-[#F5F5F0] mb-2 group-hover:text-[#C5A059] transition-colors">
                Originals &amp; Limited Prints Now Available
              </h4>
              <p className="text-[#A3A3A3] text-sm line-clamp-3 mb-4">
                Phaeton Imagery launches its online shop — original artworks and 
                signed limited-edition prints available for purchase.
              </p>
              <span className="inline-flex items-center gap-2 text-[#C5A059] font-mono text-[10px] uppercase tracking-[0.2em]">
                Browse Shop <ArrowRight size={12} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#121212] border-t border-white/5">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <h3 className="font-serif text-2xl text-[#F5F5F0] mb-4">Press Inquiries</h3>
          <p className="text-[#A3A3A3] text-sm mb-6">
            For press inquiries, interviews, or collaboration requests, please reach out directly.
          </p>
          <a
            href="mailto:gabodunrin@gmail.com?subject=Press Inquiry — Phaeton Imagery"
            className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0A0A0A] px-8 py-4 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#E0BE77] transition-colors"
            data-testid="press-contact-btn"
          >
            Contact for Press
          </a>
        </div>
      </section>
    </div>
  );
}
